import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  Box,
  Fab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import confetti from 'canvas-confetti';
import { getChallenges, createChallenge, updateChallenge, deleteChallenge } from '../api/challengesApi';

const MAX_TASKS = 5;

const getEndOfWeekDateTimeLocal = () => {
  const now = new Date();
  const daysUntilSunday = (7 - now.getDay()) % 7 || 7;
  const end = new Date(now);
  end.setDate(now.getDate() + daysUntilSunday);
  end.setHours(23, 59, 0, 0);
  const pad = (v) => String(v).padStart(2, '0');
  return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`;
};

const deadlineColor = (deadline, completed) => {
  if (completed) return 'rgba(226,232,240,0.35)';
  if (!deadline) return 'rgba(226,232,240,0.55)';
  const hours = (new Date(deadline) - Date.now()) / 3_600_000;
  if (hours < 0) return '#f87171';
  if (hours < 24) return '#fb923c';
  if (hours < 72) return '#facc15';
  return 'rgba(226,232,240,0.55)';
};

const DEFAULT_ACCOUNTABILITY = 'Get humiliated by AI';
const accountabilityOptions = ['Pay $50', 'Share challenge on social media', DEFAULT_ACCOUNTABILITY];

const ChallengesPage = ({ onProgressUpdate, onError }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [tasks, setTasks] = useState([]);
  const [taskInput, setTaskInput] = useState('');
  const [deadlineInput, setDeadlineInput] = useState(getEndOfWeekDateTimeLocal());
  const [accountabilitySelections, setAccountabilitySelections] = useState([DEFAULT_ACCOUNTABILITY]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const maxReached = tasks.length >= MAX_TASKS;

  useEffect(() => {
    getChallenges()
      .then((stored) => { if (Array.isArray(stored)) setTasks(stored); })
      .catch(() => onError?.('Couldn\'t load challenges. Check your connection.'));

    // Pre-fill dialog from the landing page "try it" form
    const pending = localStorage.getItem('pending_challenge');
    if (pending) {
      try {
        const { text, accountability: acc } = JSON.parse(pending);
        localStorage.removeItem('pending_challenge');
        if (text?.trim()) {
          setTaskInput(text);
          setAccountabilitySelections(Array.isArray(acc) && acc.length ? acc : [DEFAULT_ACCOUNTABILITY]);
          setDeadlineInput(getEndOfWeekDateTimeLocal());
          setEditingIndex(null);
          setDialogOpen(true);
        }
      } catch {}
    }
  }, [onError]);

  // Notify parent of progress whenever tasks change
  useEffect(() => {
    onProgressUpdate?.(tasks.filter((t) => t.completed).length, tasks.length);
  }, [tasks, onProgressUpdate]);

  const openDialog = () => {
    if (maxReached) return;
    setTaskInput('');
    setDeadlineInput(getEndOfWeekDateTimeLocal());
    setAccountabilitySelections([DEFAULT_ACCOUNTABILITY]);
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleEditTask = (index) => {
    const task = tasks[index];
    setTaskInput(task.text);
    setDeadlineInput(task.deadline || getEndOfWeekDateTimeLocal());
    setAccountabilitySelections(task.accountability || []);
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const toggleAccountability = (option) => {
    setAccountabilitySelections((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const handleSaveTask = async () => {
    if (!taskInput.trim()) return;
    try {
      if (editingIndex !== null) {
        const updated = [...tasks];
        const updatedTask = {
          ...updated[editingIndex],
          text: taskInput,
          deadline: deadlineInput,
          accountability: accountabilitySelections
        };
        await updateChallenge(updatedTask.id, updatedTask);
        updated[editingIndex] = updatedTask;
        setTasks(updated);
      } else {
        const saved = await createChallenge({
          text: taskInput,
          deadline: deadlineInput,
          accountability: accountabilitySelections,
          completed: false
        });
        setTasks((prev) => [...prev, saved]);
      }
      closeDialog();
    } catch {
      onError?.('Couldn\'t save challenge. Try again.');
    }
  };

  const handleToggleComplete = async (index) => {
    const wasCompleted = tasks[index].completed;
    const updated = [...tasks];
    updated[index] = { ...updated[index], completed: !wasCompleted };
    try {
      await updateChallenge(updated[index].id, updated[index]);
      setTasks(updated);
      if (!wasCompleted) confetti({ particleCount: 90, spread: 65, origin: { y: 0.6 } });
    } catch {
      onError?.('Couldn\'t update challenge.');
    }
  };

  const handleDeleteTask = async (index) => {
    try {
      await deleteChallenge(tasks[index].id);
      setTasks((prev) => prev.filter((_, i) => i !== index));
    } catch {
      onError?.('Couldn\'t delete challenge.');
    }
  };

  return (
    <>
      <Typography
        variant="caption"
        component="p"
        sx={{
          mb: 2,
          color: 'rgba(226,232,240,0.5)',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          fontWeight: 600,
          fontSize: '0.68rem',
        }}
      >
        Active challenges
      </Typography>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#0f172a',
            backgroundImage: 'none',
            ...(isMobile && {
              m: 0,
              borderRadius: 0,
              maxHeight: '100dvh'
            })
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1,
            ...(isMobile && { pt: 'calc(env(safe-area-inset-top, 0px) + 16px)' })
          }}
        >
          {isMobile && (
            <IconButton edge="start" onClick={closeDialog} sx={{ mr: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          {editingIndex !== null ? 'Edit challenge' : 'New challenge'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus={!isMobile}
            variant="outlined"
            label="Challenge"
            placeholder="What will you accomplish?"
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveTask()}
            fullWidth
            sx={{ mt: 1, mb: 2.5 }}
            inputProps={{ enterKeyHint: 'done' }}
          />
          <TextField
            label="Deadline"
            type="datetime-local"
            value={deadlineInput}
            onChange={(e) => setDeadlineInput(e.target.value)}
            fullWidth
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { style: { colorScheme: 'dark' } }
            }}
            helperText="Defaults to end of this week"
            sx={{ mb: 0.5 }}
          />
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'rgba(226,232,240,0.75)' }}>
            Accountability if not done on time
          </Typography>
          <FormGroup>
            {accountabilityOptions.map((option) => (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox
                    checked={accountabilitySelections.includes(option)}
                    onChange={() => toggleAccountability(option)}
                    sx={{ py: 1 }}
                  />
                }
                label={option}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: isMobile
              ? 'calc(env(safe-area-inset-bottom, 0px) + 16px)'
              : 2,
            gap: 1
          }}
        >
          {!isMobile && (
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSaveTask}
            disabled={!taskInput.trim()}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 700, py: 1.25 }}
          >
            Save challenge
          </Button>
        </DialogActions>
      </Dialog>

      {tasks.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.4)', mb: 0.5 }}>
            No challenges yet.
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.28)' }}>
            Tap + below to add your first one.
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {tasks.map((task, index) => (
            <ListItem
              key={task.id || index}
              disablePadding
              sx={{
                mb: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.06)',
                overflow: 'hidden'
              }}
              secondaryAction={
                <Stack direction="row" spacing={0}>
                  <IconButton
                    edge="end"
                    onClick={() => handleToggleComplete(index)}
                    sx={{
                      color: task.completed ? '#4ade80' : 'rgba(226,232,240,0.4)',
                      minWidth: 44,
                      minHeight: 44
                    }}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteTask(index)}
                    sx={{ color: 'rgba(226,232,240,0.4)', minWidth: 44, minHeight: 44 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemButton
                onClick={() => handleEditTask(index)}
                sx={{ py: 1.5, pr: '96px' }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      sx={{
                        textDecoration: task.completed ? 'line-through' : 'none',
                        color: task.completed ? 'rgba(226,232,240,0.4)' : '#e2e8f0'
                      }}
                    >
                      {task.text}
                    </Typography>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{ color: deadlineColor(task.deadline, task.completed) }}
                      >
                        {task.deadline
                          ? `Due ${new Date(task.deadline).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                          : 'No deadline'}
                        {task.accountability?.length
                          ? ` · ${task.accountability.join(', ')}`
                          : ''}
                      </Typography>
                      {(() => {
                        if (task.completed || !task.createdAt) return null;
                        const ageInDays = (Date.now() - new Date(task.createdAt).getTime()) / 86_400_000;
                        if (ageInDays < 7) return null;
                        const weeks = Math.floor(ageInDays / 7);
                        return (
                          <Typography component="span" variant="caption" sx={{ color: '#fb923c' }}>
                            ⚠ {weeks}w old
                          </Typography>
                        );
                      })()}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}

      {/* FAB — fixed above the bottom tab bar */}
      <Tooltip
        title={maxReached ? `Limit of ${MAX_TASKS} challenges reached` : 'Add challenge'}
        enterDelay={200}
        arrow
      >
        <span
          style={{
            position: 'fixed',
            right: 20,
            bottom: 'calc(48px + env(safe-area-inset-bottom, 0px) + 14px)',
            zIndex: 40,
          }}
        >
          <Fab
            color="primary"
            onClick={openDialog}
            disabled={maxReached}
            size="medium"
            sx={{
              bgcolor: '#0ea5e9',
              boxShadow: '0 4px 18px rgba(14,165,233,0.45)',
              '&:hover': { bgcolor: '#0284c7' },
              '&:active': { transform: 'scale(0.93)' },
              '&.Mui-disabled': {
                bgcolor: 'rgba(226,232,240,0.1)',
                color: 'rgba(226,232,240,0.3)',
                boxShadow: 'none',
              },
              transition: 'background-color 0.15s, transform 0.1s',
            }}
          >
            <AddIcon />
          </Fab>
        </span>
      </Tooltip>
    </>
  );
};

export default ChallengesPage;
